import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { HERO_IMAGE, LUXURY_IMAGES, INDUSTRIES, CATEGORY_META, num, currency } from "@/lib/constants";
import MapView from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowRight, Sparkles, Database, Zap, Shield, MapPin, TrendingUp,
  Search, ShieldCheck, Clock, DollarSign, Star, Quote,
} from "lucide-react";

const AnimatedNumber = ({ target, prefix = "", suffix = "" }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = Math.max(1, Math.ceil(target / 60));
    const t = setInterval(() => {
      start += step;
      if (start >= target) { setN(target); clearInterval(t); } else setN(start);
    }, 24);
    return () => clearInterval(t);
  }, [target]);
  return <span className="font-mono">{prefix}{n.toLocaleString()}{suffix}</span>;
};

const TESTIMONIALS = [
  {
    name: "Marcus L.",
    role: "Principal, Reliant Capital Partners",
    quote: "We sourced two off-market industrial acquisitions in the first 30 days. The Close Out paid for itself 40x over.",
    stars: 5,
  },
  {
    name: "Priya K.",
    role: "CRE Broker, Colvin & Associates",
    quote: "The AI scoring saved my team hours of underwriting. We now filter to the top 5% of distress deals only.",
    stars: 5,
  },
  {
    name: "Derek O.",
    role: "Founder, Ridgeline Business Brokerage",
    quote: "The WARN + bankruptcy feed alone is a 10x upgrade over what I was cobbling together from public records.",
    stars: 5,
  },
];

const FAQ = [
  { q: "How is this different from CoStar or Reonomy?", a: "CoStar and Reonomy track healthy listings. The Close Out tracks distress — foreclosures, business closures, tax delinquencies, WARN notices, and bankruptcy filings — the deals nobody else surfaces. AI ranks them so you skip the noise." },
  { q: "Do I need a card to start?", a: "Yes, to reserve your seat, but you are not charged for 7 days. Cancel anytime before day 8 and you owe nothing." },
  { q: "How fresh is the data?", a: "Foreclosures, sheriff sales, and bankruptcy filings refresh daily. Business closures and WARN notices refresh weekly. Every record is timestamped." },
  { q: "Can I export deals?", a: "Yes — CSV, Excel, JSON, and branded PDF investment memos on Broker+ plans." },
  { q: "Is there an API?", a: "Enterprise and Institutional include full REST API access with a 10-req/sec rate limit." },
];

export default function LandingPage() {
  const nav = useNavigate();
  const [stats, setStats] = useState({ total_opportunities: 240, states_covered: 50, data_sources: 240, categories: 12, by_category: [] });
  const [recent, setRecent] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [dealsPerMonth, setDealsPerMonth] = useState(3);
  const [avgCommission, setAvgCommission] = useState(25000);
  const [showStickyBar, setShowStickyBar] = useState(false);

  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data));
    api.get("/dashboard/top-opportunities?limit=6").then((r) => setRecent(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setShowStickyBar(window.scrollY > 800);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitContact = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", { ...form, request_type: "demo" });
      toast.success("Request received. Our team will be in touch within 24 hours.");
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch { toast.error("Something went wrong. Try again."); }
  };

  const monthlyValue = dealsPerMonth * avgCommission;
  const yearlyValue = monthlyValue * 12;
  const brokerCost = 299;
  const roiMultiple = Math.round(monthlyValue / brokerCost);

  return (
    <div className="min-h-screen bg-[#14100c] text-[#f4ede2] relative overflow-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 border-b border-[#33291f]/50 backdrop-blur-xl bg-[#14100c]/60">
        <div className="max-w-[1440px] mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <div className="w-9 h-9 rounded bg-gradient-to-br from-[#d4a574] to-[#a67b4c] flex items-center justify-center text-[#14100c] font-black font-display">C</div>
            <div>
              <div className="font-display font-bold text-[16px] tracking-tight">The Close Out</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#7a6d5a]">Distress Intelligence</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-[13px] text-[#b8ac96]">
            <a href="#features" className="hover:text-[#f4ede2]">Features</a>
            <a href="#industries" className="hover:text-[#f4ede2]">Industries</a>
            <a href="#map" className="hover:text-[#f4ede2]">Live Map</a>
            <a href="#roi" className="hover:text-[#f4ede2]">ROI</a>
            <Link to="/pricing" data-testid="landing-pricing-link" className="hover:text-[#f4ede2]">Pricing</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="landing-login-btn" className="text-[13px] px-3 py-1.5 text-[#b8ac96] hover:text-[#f4ede2]">Sign In</Link>
            <Link to="/register" data-testid="landing-trial-btn" className="text-[13px] px-4 py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold hover:bg-[#e8b988]">Start Free Trial</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[100vh] flex items-end pb-24 pt-32">
        <img src={HERO_IMAGE} alt="Luxury residence" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-scrim" />
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-[1440px] mx-auto px-8 w-full">
          <div className="max-w-3xl fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#34d399] pin-pulse" style={{ color: "#34d399" }} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] font-semibold">Live · <AnimatedNumber target={stats.total_opportunities} /> opportunities tracked</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight mb-6">
              Find Opportunities<br />
              <span className="italic font-normal text-[#d4a574]">Before Everyone Else.</span>
            </h1>
            <p className="text-[17px] text-[#b8ac96] max-w-xl leading-relaxed mb-6">
              The Bloomberg Terminal for distressed businesses, commercial real estate, foreclosures, tax liens, and investment opportunities across the United States — indexed, scored, and mapped by AI.
            </p>
            <div className="flex flex-wrap gap-3 mb-6">
              <Link to="/register" data-testid="hero-trial-btn" className="inline-flex items-center gap-2 px-6 py-3.5 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[14px] hover:bg-[#e8b988] shadow-lg shadow-[#d4a574]/20">
                Start 7-Day Free Trial <ArrowRight size={16} />
              </Link>
              <a href="#demo" data-testid="hero-demo-btn" className="inline-flex items-center gap-2 px-5 py-3.5 rounded border border-[#4a3a2b] text-[#f4ede2] text-[14px] hover:border-[#d4a574]">
                Book a Demo
              </a>
            </div>
            <div className="flex items-center gap-5 flex-wrap text-[11px] text-[#b8ac96]">
              <div className="flex items-center gap-1.5"><ShieldCheck size={13} className="text-[#34d399]" /> No credit-card charge for 7 days</div>
              <div className="flex items-center gap-1.5"><ShieldCheck size={13} className="text-[#34d399]" /> Cancel anytime</div>
              <div className="flex items-center gap-1.5"><ShieldCheck size={13} className="text-[#34d399]" /> Secure Stripe checkout</div>
            </div>
          </div>

          {/* Stats strip */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#33291f]/50 border border-[#33291f]/50 rounded-lg overflow-hidden max-w-4xl">
            {[
              { label: "Live Opportunities", value: stats.total_opportunities },
              { label: "US States", value: stats.states_covered },
              { label: "Distress Categories", value: stats.categories },
              { label: "Data Sources", value: stats.data_sources },
            ].map((s) => (
              <div key={s.label} className="bg-[#14100c] p-5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] mb-2">{s.label}</div>
                <div className="font-display text-3xl md:text-4xl font-bold text-[#f4ede2]">
                  <AnimatedNumber target={s.value} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Bar — Industries served */}
      <section className="border-y border-[#33291f] bg-[#1c1712]/50 py-8">
        <div className="max-w-[1440px] mx-auto px-8">
          <div className="text-center text-[10px] uppercase tracking-[0.25em] text-[#7a6d5a] mb-5">Trusted by professionals across</div>
          <div className="flex items-center justify-center gap-x-10 gap-y-3 flex-wrap text-[13px] text-[#b8ac96] font-display">
            {["CRE Brokerages", "Private Equity", "Family Offices", "Business Brokers", "Property Developers", "Distressed Debt Funds", "Auction Firms", "Turnaround Consultants"].map((n) => (
              <div key={n} className="hover:text-[#d4a574] [transition:color_200ms_ease]">{n}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Map preview */}
      <section id="map" className="max-w-[1440px] mx-auto px-8 py-24">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Live Intelligence Map</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Every distressed asset. One map.</h2>
          </div>
          <div className="flex gap-4 flex-wrap text-[11px] text-[#b8ac96]">
            {Object.entries(CATEGORY_META).slice(0, 6).map(([k, m]) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color, boxShadow: `0 0 8px ${m.color}` }} />
                {m.label}
              </div>
            ))}
          </div>
        </div>
        <MapView height={560} />
      </section>

      {/* Top opportunities */}
      <section className="max-w-[1440px] mx-auto px-8 py-24">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Trending Now</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-10">Top-scoring opportunities today</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {recent.slice(0, 6).map((o) => (
            <div key={o.id} className="border border-[#33291f] rounded-lg overflow-hidden bg-[#1c1712]">
              <div className="aspect-[16/10] relative">
                <img src={o.image_url} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#14100c] via-transparent to-transparent" />
                <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 px-2 py-1 rounded bg-[#14100c]/90 text-[10px] uppercase tracking-wider font-semibold" style={{ color: CATEGORY_META[o.category]?.color }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: CATEGORY_META[o.category]?.color }} />
                  {CATEGORY_META[o.category]?.label}
                </div>
              </div>
              <div className="p-4">
                <div className="font-display text-[15px] font-semibold line-clamp-1">{o.title}</div>
                <div className="text-[11px] text-[#7a6d5a] mt-1">{o.city}, {o.state} · AI Score <span className="text-[#d4a574] font-mono">{Math.round(o.ai_score?.opportunity)}</span></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-[1440px] mx-auto px-8 py-24 border-t border-[#33291f]">
        <div className="text-center mb-14">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Customer Voices</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Deal-flow professionals close faster.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} data-testid={`testimonial-${i}`} className="p-6 border border-[#33291f] rounded-lg bg-[#1c1712] relative">
              <Quote size={28} className="text-[#d4a574]/30 absolute top-4 right-4" />
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: t.stars }).map((_, j) => (
                  <Star key={j} size={14} className="fill-[#d4a574] text-[#d4a574]" />
                ))}
              </div>
              <p className="text-[14px] text-[#f4ede2] leading-relaxed mb-5">"{t.quote}"</p>
              <div className="border-t border-[#33291f] pt-4">
                <div className="font-display font-semibold text-[13px]">{t.name}</div>
                <div className="text-[11px] text-[#7a6d5a]">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ROI Calculator */}
      <section id="roi" className="border-t border-[#33291f] py-24">
        <div className="max-w-5xl mx-auto px-8">
          <div className="text-center mb-10">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">ROI Calculator</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">One extra deal pays for it 80x over.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="p-6 border border-[#33291f] rounded-lg bg-[#1c1712] space-y-6">
              <div>
                <div className="flex justify-between text-[12px] text-[#b8ac96] mb-2">
                  <span>Deals sourced per month via The Close Out</span>
                  <span className="font-mono text-[#d4a574] font-semibold" data-testid="roi-deals-value">{dealsPerMonth}</span>
                </div>
                <input type="range" min="1" max="20" value={dealsPerMonth} onChange={(e) => setDealsPerMonth(Number(e.target.value))} data-testid="roi-deals-slider" className="w-full accent-[#d4a574]" />
              </div>
              <div>
                <div className="flex justify-between text-[12px] text-[#b8ac96] mb-2">
                  <span>Average commission or profit per deal</span>
                  <span className="font-mono text-[#d4a574] font-semibold" data-testid="roi-commission-value">{currency(avgCommission)}</span>
                </div>
                <input type="range" min="1000" max="250000" step="1000" value={avgCommission} onChange={(e) => setAvgCommission(Number(e.target.value))} data-testid="roi-commission-slider" className="w-full accent-[#d4a574]" />
              </div>
            </div>
            <div className="space-y-4">
              <div className="p-5 border border-[#34d399]/30 rounded-lg bg-[#34d399]/5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#34d399] font-semibold mb-1">Estimated Monthly Value</div>
                <div className="font-display text-4xl font-bold text-[#34d399]" data-testid="roi-monthly">{currency(monthlyValue)}</div>
              </div>
              <div className="p-5 border border-[#d4a574]/30 rounded-lg bg-[#d4a574]/5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] font-semibold mb-1">Estimated Annual Value</div>
                <div className="font-display text-4xl font-bold text-[#d4a574]" data-testid="roi-annual">{currency(yearlyValue)}</div>
              </div>
              <div className="p-4 border border-[#33291f] rounded-lg bg-[#1c1712]">
                <div className="text-[11px] text-[#b8ac96]">Broker plan cost: <span className="font-mono text-[#f4ede2]">$299/mo</span> · Estimated ROI: <span className="font-mono text-[#34d399] font-bold" data-testid="roi-multiple">{roiMultiple}x</span></div>
              </div>
              <Link to="/register" data-testid="roi-cta" className="block text-center w-full py-3.5 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[14px] hover:bg-[#e8b988]">
                Start My 7-Day Free Trial →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="max-w-[1440px] mx-auto px-8 py-24 border-t border-[#33291f]">
        <div className="grid md:grid-cols-2 gap-16">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Industries Served</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-6">Built for professionals who move fast.</h2>
            <p className="text-[#b8ac96] leading-relaxed">From private equity to independent contractors, teams use The Close Out to source acquisitions, secure vacant real estate, buy equipment at auction, and identify emerging markets before competitors.</p>
          </div>
          <div className="grid grid-cols-2 gap-px bg-[#33291f]/50 border border-[#33291f]/50 rounded-lg overflow-hidden">
            {INDUSTRIES.map((i) => (
              <div key={i} className="bg-[#1c1712] p-4 text-[13px] hover:bg-[#241d16] cursor-default">{i}</div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Features */}
      <section id="features" className="max-w-[1440px] mx-auto px-8 py-24 border-t border-[#33291f]">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">AI Capabilities</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-12">Ten scores. One decision.</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: Sparkles, title: "Opportunity Score", body: "Composite 0-100 rank across signal quality, distress severity, market timing, and asset condition." },
            { icon: TrendingUp, title: "Investment Potential", body: "Forecasted return factoring comparable exits, market comps, and macroeconomic indicators." },
            { icon: Shield, title: "Risk Level", body: "Multi-factor risk classification: title issues, litigation exposure, environmental, market volatility." },
            { icon: Database, title: "Business Health", body: "Operational solvency estimate from public filings, WARN notices, court records, credit signals." },
            { icon: MapPin, title: "Neighborhood Score", body: "Location grade based on comps, demographics, development trajectory, distress density." },
            { icon: Zap, title: "Natural Language Search", body: "Ask questions like a human. 'Find warehouses over 100K sqft with tax delinquency near Seattle.'" },
          ].map((f) => (
            <div key={f.title} className="p-6 border border-[#33291f] rounded-lg bg-gradient-to-br from-[#1c1712] to-[#14100c] hover:border-[#d4a574]/40 [transition:border-color_200ms_ease]">
              <div className="w-10 h-10 rounded bg-[#241d16] border border-[#4a3a2b] flex items-center justify-center mb-4">
                <f.icon size={18} className="text-[#d4a574]" />
              </div>
              <div className="font-display text-lg font-semibold mb-2">{f.title}</div>
              <div className="text-[13px] text-[#b8ac96] leading-relaxed">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing preview */}
      <section id="pricing" className="max-w-[1440px] mx-auto px-8 py-24 border-t border-[#33291f]">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Pricing</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-3">Start free. Cancel anytime.</h2>
          <p className="text-[#b8ac96]">7-day free trial on Broker and Enterprise. No credit-card charge until day 8.</p>
        </div>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/pricing" data-testid="landing-pricing-cta" className="inline-flex items-center gap-2 px-6 py-3.5 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[14px] hover:bg-[#e8b988]">See full pricing <ArrowRight size={16} /></Link>
          <Link to="/register" data-testid="landing-register-cta" className="inline-flex items-center gap-2 px-6 py-3.5 rounded border border-[#4a3a2b] text-[#f4ede2] text-[14px] hover:border-[#d4a574]">Create Free Account</Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-[#33291f] py-24">
        <div className="max-w-3xl mx-auto px-8">
          <div className="text-center mb-10">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">FAQ</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Common questions.</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="group border border-[#33291f] rounded-lg bg-[#1c1712] p-5 hover:border-[#4a3a2b] [transition:border-color_200ms_ease]" data-testid={`landing-faq-${i}`}>
                <summary className="cursor-pointer text-[15px] font-semibold text-[#f4ede2] flex justify-between items-center list-none">
                  {f.q}
                  <span className="text-[#d4a574] text-xl group-open:rotate-45 [transition:transform_150ms_ease]">+</span>
                </summary>
                <p className="mt-3 text-[13px] text-[#b8ac96] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / Demo */}
      <section id="demo" className="relative py-24 border-t border-[#33291f]">
        <img src={LUXURY_IMAGES[1]} className="absolute inset-0 w-full h-full object-cover opacity-30" alt="" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#14100c] via-[#14100c]/85 to-[#14100c]" />
        <div className="relative max-w-3xl mx-auto px-8">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold text-center">Talk to Sales</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-center mb-3">Prefer a walkthrough?</h2>
          <p className="text-center text-[#b8ac96] mb-10">15-minute demo. Live data. Custom to your market. Or just <Link to="/register" className="text-[#d4a574] underline">start your free trial</Link> right now.</p>
          <form onSubmit={submitContact} className="space-y-4 glass p-8 rounded-lg" data-testid="demo-form">
            <div className="grid md:grid-cols-2 gap-4">
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="demo-name" className="bg-[#1c1712] border-[#33291f]" />
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Work email" data-testid="demo-email" className="bg-[#1c1712] border-[#33291f]" />
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company" data-testid="demo-company" className="bg-[#1c1712] border-[#33291f]" />
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" data-testid="demo-phone" className="bg-[#1c1712] border-[#33291f]" />
            </div>
            <Textarea rows={4} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your use case, target markets, team size…" data-testid="demo-message" className="bg-[#1c1712] border-[#33291f]" />
            <button type="submit" data-testid="demo-submit" className="w-full py-3 rounded bg-[#d4a574] text-[#14100c] font-semibold hover:bg-[#e8b988]">Request Demo →</button>
          </form>
        </div>
      </section>

      <footer className="border-t border-[#33291f] py-8 text-center text-[11px] text-[#7a6d5a] space-y-1">
        <div>© 2026 The Close Out · Distress Intelligence Platform · Built for professionals</div>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Link to="/terms" data-testid="footer-terms" className="hover:text-[#d4a574]">Terms of Service</Link>
          <span>·</span>
          <Link to="/privacy" data-testid="footer-privacy" className="hover:text-[#d4a574]">Privacy Policy</Link>
          <span>·</span>
          <a href="mailto:support@thecloseout.io" className="hover:text-[#d4a574]">Contact</a>
        </div>
        <div className="font-mono text-[10px]">The Close Out · EIN 42-4006558 · Graham, WA · thecloseout.io</div>
      </footer>

      {/* Sticky bottom CTA bar */}
      {showStickyBar && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-[#14100c]/95 backdrop-blur-lg border-t border-[#d4a574]/30 py-3 px-8" data-testid="sticky-cta-bar">
          <div className="max-w-[1440px] mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex w-8 h-8 rounded bg-gradient-to-br from-[#d4a574] to-[#a67b4c] items-center justify-center text-[#14100c] font-black text-xs">C</div>
              <div>
                <div className="font-display font-semibold text-[13px]">Start your 7-day free trial</div>
                <div className="text-[11px] text-[#7a6d5a]">No charge until day 8 · Cancel anytime</div>
              </div>
            </div>
            <Link to="/register" data-testid="sticky-cta-btn" className="px-5 py-2.5 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[13px] hover:bg-[#e8b988] whitespace-nowrap">Start Free Trial →</Link>
          </div>
        </div>
      )}
    </div>
  );
}
